import React, { useEffect, useMemo, useRef, useState } from "react";
import heatmapFactory, { HeatmapDataPoint, HeatmapInstance } from "heatmap.js";
import { PassMapSide, PassMapStatus } from "../passMap/helper";
import { CategoryTabs } from "../components/CategoryTabs";
import { PitchLayout } from "../components/PitchLayout";
import { heatmapMocks } from "./mockHeatmapData";
import MinuteSlider from "../xgMap/components/MinuteSlider";

const HALF_CUTOFF_MINUTE = 45;

type HalfSelection = "full" | "first" | "second";

const HEATMAP_SIDE_TABS: { id: PassMapSide; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "away", label: "Away" },
];

export type HeatMapProps = {
  status?: PassMapStatus;
};

export const HeatMap: React.FC<HeatMapProps> = ({ status }) => {
  const [activeSide, setActiveSide] = useState<PassMapSide>("home");
  const heatmapData = heatmapMocks[activeSide];

  const sliderMax = Math.max(heatmapData.timeline.maxMinute, 90);
  const endMinute = sliderMax;
  const liveMinute = heatmapData.timeline.minute;
  const [timeCut, setTimeCut] = useState(sliderMax);
  const [selectedHalf] = useState<HalfSelection>("full");
  const heatmapContainerRef = useRef<HTMLDivElement | null>(null);
  const heatmapInstanceRef = useRef<HeatmapInstance | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const { width: containerWidth, height: containerHeight } = containerSize;

  useEffect(() => {
    setTimeCut((previous) => Math.min(previous, sliderMax));
  }, [sliderMax]);

  useEffect(() => {
    setTimeCut((previous) => {
      if (selectedHalf === "first") {
        return Math.min(previous, HALF_CUTOFF_MINUTE);
      }
      if (selectedHalf === "second") {
        return Math.max(previous, HALF_CUTOFF_MINUTE);
      }
      return previous;
    });
  }, [selectedHalf]);

  useEffect(() => {
    const container = heatmapContainerRef.current;
    if (!container) {
      return undefined;
    }

    const instance = heatmapFactory.create({
      container,
      radius: 35,
      maxOpacity: 0.9,
      minOpacity: 0,
      blur: 0.85,
      gradient: {
        0.1: "#1635FF",
        0.45: "#16D4FF",
        0.6: "#00FF5E",
        0.8: "#FBFF00",
        1: "#FF0000",
      },
    });
    heatmapInstanceRef.current = instance;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      setContainerSize((previous) => {
        if (
          previous.width === Math.floor(rect.width) &&
          previous.height === Math.floor(rect.height)
        ) {
          return previous;
        }
        return {
          width: rect.width,
          height: rect.height,
        };
      });
    };

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSize);
      observer.observe(container);
      updateSize();

      return () => {
        observer.disconnect();
        container.innerHTML = "";
        heatmapInstanceRef.current = null;
      };
    }

    window.addEventListener("resize", updateSize);
    updateSize();

    return () => {
      window.removeEventListener("resize", updateSize);
      container.innerHTML = "";
      heatmapInstanceRef.current = null;
    };
  }, []);

  const filteredPoints = useMemo(() => {
    return heatmapData.points.filter((point) => {
      if (selectedHalf === "first" && point.minute > HALF_CUTOFF_MINUTE) {
        return false;
      }
      if (selectedHalf === "second" && point.minute <= HALF_CUTOFF_MINUTE) {
        return false;
      }
      return point.minute <= timeCut;
    });
  }, [heatmapData.points, selectedHalf, timeCut]);

  const heatmapPoints = useMemo<HeatmapDataPoint[]>(() => {
    if (!containerWidth || !containerHeight) {
      return [];
    }
    return filteredPoints.map((point) => ({
      x: (point.x / 100) * containerWidth,
      y: (point.y / 100) * containerHeight,
      value: point.value,
    }));
  }, [filteredPoints, containerWidth, containerHeight]);

  useEffect(() => {
    const instance = heatmapInstanceRef.current;
    if (!instance || containerWidth === 0 || containerHeight === 0) {
      return;
    }

    if (instance.configure) {
      instance.configure({
        radius: Math.max(containerWidth, containerHeight) / 22,
      });
    }

    instance.setData({
      max: heatmapData.maxValue,
      min: 0,
      data: heatmapPoints,
    });
  }, [heatmapPoints, containerWidth, containerHeight, heatmapData.maxValue]);

  const heatmapStatus = status ?? "default";

  return (
    <div
      data-heatmap-status={heatmapStatus}
      className="flex flex-col w-full overflow-hidden"
    >
      <CategoryTabs
        tabs={HEATMAP_SIDE_TABS}
        selectedCategory={activeSide}
        onSelectCategory={setActiveSide}
      />

      <PitchLayout>
        <div className="absolute inset-0 pointer-events-none z-[2]">
          <div ref={heatmapContainerRef} className="relative h-full w-full" />
        </div>
      </PitchLayout>

      <MinuteSlider
        liveMinute={liveMinute}
        endMinute={endMinute}
        value={timeCut}
        onChange={(v) => setTimeCut(v)}
        labels={{ left: "0:00", mid: "FH", right: `${endMinute}:00` }}
      />
    </div>
  );
};

export default HeatMap;
